import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { MortgageNoteInputs, MortgageNoteCalculations, SavedAnalysis, AnalysisId } from '../types';
import { calculateMortgageNote } from '../utilities/calculations';
import {
  generateAnalysisId,
  createDefaultAnalysis,
  saveAnalysesToFile,
  loadAnalysesFromFile,
} from '../utilities/fileStorage';

interface NoteContextType {
  // Existing API (derived from active analysis)
  inputs: MortgageNoteInputs;
  calculations: MortgageNoteCalculations;
  updateInput: (field: keyof MortgageNoteInputs, value: string | number) => void;
  updateNumberInput: (field: keyof MortgageNoteInputs, value: string) => void;
  hasRequiredInputs: boolean;
  totalFees: number;

  // Multi-analysis management
  analyses: SavedAnalysis[];
  activeAnalysisId: AnalysisId | null;
  createNewAnalysis: () => AnalysisId;
  switchAnalysis: (id: AnalysisId) => void;
  deleteAnalysis: (id: AnalysisId) => void;
  duplicateAnalysis: (id: AnalysisId) => AnalysisId;
  getAnalysisDisplayName: (id: AnalysisId) => string;

  // File persistence
  saveToFile: () => Promise<void>;
  loadFromFile: () => Promise<void>;
}

const NoteContext = createContext<NoteContextType | undefined>(undefined);

export const useNoteContext = () => {
  const context = useContext(NoteContext);
  if (!context) {
    throw new Error('useNoteContext must be used within a NoteProvider');
  }
  return context;
};

interface NoteProviderProps {
  children: ReactNode;
}

export const NoteProvider: React.FC<NoteProviderProps> = ({ children }) => {
  // Initialize with a single default analysis - use lazy initialization
  const [{ analysisMap, activeAnalysisId }, setAnalysisState] = useState(() => {
    const defaultAnalysis = createDefaultAnalysis();
    return {
      analysisMap: new Map([[defaultAnalysis.id, defaultAnalysis]]),
      activeAnalysisId: defaultAnalysis.id as AnalysisId | null,
    };
  });

  // Helper to update just the map
  const setAnalysisMap = useCallback((updater: (prev: Map<AnalysisId, SavedAnalysis>) => Map<AnalysisId, SavedAnalysis>) => {
    setAnalysisState((prev) => ({
      ...prev,
      analysisMap: updater(prev.analysisMap),
    }));
  }, []);

  // Helper to update just the active ID
  const setActiveAnalysisId = useCallback((id: AnalysisId | null) => {
    setAnalysisState((prev) => ({
      ...prev,
      activeAnalysisId: id,
    }));
  }, []);

  // Get the active analysis
  const activeAnalysis = activeAnalysisId ? analysisMap.get(activeAnalysisId) : null;
  const inputs = activeAnalysis?.inputs || createDefaultAnalysis().inputs;

  // Calculate derived values
  const [calculations, setCalculations] = useState<MortgageNoteCalculations>(
    () => calculateMortgageNote(inputs)
  );

  useEffect(() => {
    setCalculations(calculateMortgageNote(inputs));
  }, [inputs]);

  // Update an input field on the active analysis
  const updateInput = useCallback((field: keyof MortgageNoteInputs, value: string | number) => {
    if (!activeAnalysisId) return;

    setAnalysisMap((prev) => {
      const newMap = new Map(prev);
      const analysis = newMap.get(activeAnalysisId);
      if (analysis) {
        newMap.set(activeAnalysisId, {
          ...analysis,
          inputs: {
            ...analysis.inputs,
            [field]: value,
          },
          updatedAt: new Date().toISOString(),
        });
      }
      return newMap;
    });
  }, [activeAnalysisId]);

  const updateNumberInput = useCallback((field: keyof MortgageNoteInputs, value: string) => {
    const numValue = parseFloat(value) || 0;
    updateInput(field, numValue);
  }, [updateInput]);

  // Create a new analysis
  const createNewAnalysis = useCallback((): AnalysisId => {
    const newAnalysis = createDefaultAnalysis();
    setAnalysisMap((prev) => {
      const newMap = new Map(prev);
      newMap.set(newAnalysis.id, newAnalysis);
      return newMap;
    });
    setActiveAnalysisId(newAnalysis.id);
    return newAnalysis.id;
  }, []);

  // Switch to a different analysis
  const switchAnalysis = useCallback((id: AnalysisId) => {
    if (analysisMap.has(id)) {
      setActiveAnalysisId(id);
    }
  }, [analysisMap]);

  // Delete an analysis
  const deleteAnalysis = useCallback((id: AnalysisId) => {
    setAnalysisMap((prev) => {
      if (prev.size <= 1) return prev; // Keep at least one analysis

      const newMap = new Map(prev);
      newMap.delete(id);

      // If deleting the active analysis, switch to the first available
      if (id === activeAnalysisId) {
        const firstId = Array.from(newMap.keys())[0];
        setActiveAnalysisId(firstId);
      }

      return newMap;
    });
  }, [activeAnalysisId]);

  // Duplicate an analysis
  const duplicateAnalysis = useCallback((id: AnalysisId): AnalysisId => {
    const original = analysisMap.get(id);
    if (!original) return createNewAnalysis();

    const newId = generateAnalysisId();
    const now = new Date().toISOString();
    const duplicate: SavedAnalysis = {
      id: newId,
      inputs: {
        ...original.inputs,
        noteName: original.inputs.noteName ? `${original.inputs.noteName} (Copy)` : 'Copy',
      },
      createdAt: now,
      updatedAt: now,
    };

    setAnalysisMap((prev) => {
      const newMap = new Map(prev);
      newMap.set(newId, duplicate);
      return newMap;
    });
    setActiveAnalysisId(newId);
    return newId;
  }, [analysisMap, createNewAnalysis]);

  // Get display name for an analysis
  const getAnalysisDisplayName = useCallback((id: AnalysisId): string => {
    const analysis = analysisMap.get(id);
    if (!analysis) return 'Unknown';
    return analysis.inputs.noteName || 'Untitled Analysis';
  }, [analysisMap]);

  // Save all analyses to file
  const saveToFile = useCallback(async (): Promise<void> => {
    const analyses = Array.from(analysisMap.values());
    await saveAnalysesToFile(analyses);
  }, [analysisMap]);

  // Load analyses from file
  const loadFromFile = useCallback(async (): Promise<void> => {
    const loadedAnalyses = await loadAnalysesFromFile();
    if (loadedAnalyses.length > 0) {
      const newMap = new Map<AnalysisId, SavedAnalysis>();
      loadedAnalyses.forEach((analysis) => {
        newMap.set(analysis.id, analysis);
      });
      // Update both map and active ID atomically
      setAnalysisState({
        analysisMap: newMap,
        activeAnalysisId: loadedAnalyses[0].id,
      });
    }
  }, []);

  // Computed properties
  const hasRequiredInputs = inputs.listedPurchasePrice > 0 && inputs.principalAndInterest > 0;

  const totalFees =
    inputs.tradeDeskFee +
    inputs.monthlyServicingFee * inputs.paymentsRemaining +
    inputs.titlingFees +
    inputs.otherOneTimeFee +
    inputs.otherMonthlyFee * inputs.paymentsRemaining;

  // Convert Map to array for context consumers
  const analyses = Array.from(analysisMap.values());

  const value: NoteContextType = {
    // Existing API
    inputs,
    calculations,
    updateInput,
    updateNumberInput,
    hasRequiredInputs,
    totalFees,

    // Multi-analysis management
    analyses,
    activeAnalysisId,
    createNewAnalysis,
    switchAnalysis,
    deleteAnalysis,
    duplicateAnalysis,
    getAnalysisDisplayName,

    // File persistence
    saveToFile,
    loadFromFile,
  };

  return <NoteContext.Provider value={value}>{children}</NoteContext.Provider>;
};
